import { useTranslation } from 'react-i18next';
import { Button, Icon } from '../../../../ui/index.js';
import { carrierMarkText } from '../../carrierMark.js';
import { formatDateTime, formatMoney } from '../../format.js';
import { aggregateProductQuantity, computeBidTotal } from '../../pricing.js';
import type { BidUnit, CarrierBid, CarrierBidQuote, RequestView } from '../../types.js';
import { CarrierInsuranceSection } from './CarrierInsuranceSection.js';
import { CarrierStatePill } from '../CarrierStatePill.js';
import { RateHighlight } from '../RateHighlight.js';

// WHY (#1060): per-unit display strings are locale-bound and live in
//   the i18n bundle. The keys are typed literals so the strict-literal
//   gate keeps non-ASCII text inside the locale files, not the component.
type UnitSuffixKey =
  | 'requests:carriers.unitSuffix.kg'
  | 'requests:carriers.unitSuffix.m3'
  | 'requests:carriers.unitSuffix.piece';

function unitSuffixKey(unit: BidUnit): UnitSuffixKey {
  if (unit === 'kg') return 'requests:carriers.unitSuffix.kg';
  if (unit === 'm3') return 'requests:carriers.unitSuffix.m3';
  return 'requests:carriers.unitSuffix.piece';
}

// WHY (#585): bid validity is anchored at `quotedAt` and lasts
//   `validityDays` whole days. `isBidExpired` compares against the
//   current wall clock; `bidValidUntil` returns the absolute deadline
//   so the card can render either "valid until <date>" (active) or
//   "not actual since <date>" (expired) without re-deriving the offset
//   downstream. An expired bid renders as a darkened, inactive block
//   (`--bg-inset` fill + red `--status-error-fg` text) so a dead bid is
//   not mistaken for the gold winner; the carrier card's gold frame +
//   "winner" badge still survive when the expired bid was the winner
//   (#2098).
function bidValidUntil(q: CarrierBidQuote): number {
  return q.quotedAt + q.validityDays * 24 * 3600 * 1000;
}

function isBidExpired(q: CarrierBidQuote, now: number): boolean {
  return now >= bidValidUntil(q);
}

export interface CarrierBidCardProps {
  bid: CarrierBid;
  requestId: string;
  // WHY (#1163): the insurance section now resolves per-scope override
  //   labels (ITEM / BOX / BOX_ITEM / BOX_GROUP) against the parent
  //   request's items + boxes, so the card needs the full RequestView.
  //   The bid table still reads only weight / volume / productQuantity
  //   directly; insurance opt-in + cargo value/currency still flow
  //   through `request.insuranceRequested` / `request.insuranceCargo*`.
  request: RequestView;
  winnerBidId: string | null;
  cheapestBidId: string | null;
  canManage: boolean;
  // WHY (#2139): on a CANNOT_TRANSPORT row the assigned logist drives the
  //   minimal revive path — add-bid (pending) + reset (rejected) only. Bid
  //   edit/delete, carrier delete, and reject stay full-manage-only.
  canRevive: boolean;
  canPick: boolean;
  fxRates: Record<string, number>;
  onAddBid: () => void;
  onEditBid: (quote: CarrierBidQuote) => void;
  onDeleteBid: (quote: CarrierBidQuote) => void;
  onReject: () => void;
  onReset: () => void;
  onDelete: () => void;
  onPickBid: (quote: CarrierBidQuote) => void;
}

export function CarrierBidCard(props: CarrierBidCardProps) {
  const { t } = useTranslation(['requests', 'common']);
  const {
    bid: c,
    requestId,
    request,
    winnerBidId,
    cheapestBidId,
    canManage,
    canRevive,
    canPick,
    fxRates,
    onAddBid,
    onEditBid,
    onDeleteBid,
    onReject,
    onReset,
    onDelete,
    onPickBid,
  } = props;
  const isRejected = c.state === 'rejected';
  const showCarrierActions = canManage || canRevive;
  // WHY (#1056): `'piece'`-unit bids need the aggregate SKU count
  //   (sum of `productQuantity` across items, null if any item is
  //   missing the count). The card aggregates it locally so the parent
  //   call site can pass the full RequestView without precomputing.
  const requestForBid = {
    weight: request.weight,
    volume: request.volume,
    productQuantity: aggregateProductQuantity(request.items),
  };
  const carrierIsWinner = c.bids.some((q) => q.id === winnerBidId);

  return (
    <div
      style={{
        background: carrierIsWinner ? 'var(--accent-1-subtle)' : 'var(--bg-1)',
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-2)',
        padding: 12,
        boxShadow: carrierIsWinner ? 'inset 3px 0 0 var(--accent-1)' : 'none',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 26,
            height: 26,
            borderRadius: 'var(--r-1)',
            background: 'var(--bg-inset)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            fontWeight: 600,
            color: 'var(--fg-2)',
            flexShrink: 0,
          }}
        >
          {carrierMarkText(c.carrier.name, c.carrier.code)}
        </span>
        <span
          style={{
            fontWeight: 500,
            fontSize: 13,
            overflowWrap: 'anywhere',
            minWidth: 0,
          }}
        >
          {c.carrier.name}
        </span>
        <span
          style={{
            marginLeft: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span style={{ fontSize: 11, color: 'var(--fg-4)' }}>{formatDateTime(c.createdAt)}</span>
          <CarrierStatePill state={c.state} />
        </span>
      </div>

      {c.rejectReason && c.state === 'rejected' && (
        <div
          style={{
            marginTop: 8,
            fontSize: 12,
            color: 'var(--status-error-fg)',
            overflowWrap: 'anywhere',
            whiteSpace: 'pre-wrap',
          }}
        >
          {c.rejectReason}
        </div>
      )}

      {c.bids.length > 0 ? (
        // INVARIANT (#569): bid list uses <div> rows, not <li>, so the
        //   parent CarriersTab carrier list keeps a clean `listitem`
        //   count (one per carrier). Per-bid keyboard semantics live on
        //   the action buttons.
        <div
          role="group"
          aria-label={t('requests:carriers.bids')}
          style={{
            margin: '10px 0 0',
            display: 'grid',
            gap: 6,
          }}
        >
          {c.bids.map((q) => {
            const isWinner = q.id === winnerBidId;
            const isCheapest = cheapestBidId !== null && q.id === cheapestBidId;
            const totals = computeBidTotal(q, requestForBid, fxRates);
            const validUntil = bidValidUntil(q);
            const expired = isBidExpired(q, Date.now());
            const expiredBg = expired ? 'var(--bg-inset)' : null;
            return (
              <div
                key={q.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '6px 8px',
                  borderRadius: 'var(--r-1)',
                  background: expiredBg ?? (isWinner ? 'var(--accent-1-subtle)' : 'var(--bg-2)'),
                  fontSize: 12,
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
                  {formatMoney(q.pricePerUnit, q.currency)}
                  {t(unitSuffixKey(q.unit))}
                </span>
                {totals && (
                  <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--fg-2)' }}>
                    {t('requests:carriers.total')}: {formatMoney(totals.total, q.currency)}
                    {totals.usdHint !== undefined && (
                      <span style={{ marginLeft: 6, color: 'var(--fg-3)' }}>
                        ≈ {formatMoney(Math.round(totals.usdHint), 'USD')}
                      </span>
                    )}
                  </span>
                )}
                {q.daysMin !== null && q.daysMax !== null && (
                  <span style={{ color: 'var(--fg-3)' }}>
                    {q.daysMin === q.daysMax
                      ? t('requests:carriers.daysShort', { days: q.daysMin })
                      : t('requests:carriers.daysShortRange', {
                          min: q.daysMin,
                          max: q.daysMax,
                        })}
                  </span>
                )}
                {q.comment && (
                  <span
                    style={{
                      color: 'var(--fg-2)',
                      overflowWrap: 'anywhere',
                      whiteSpace: 'pre-wrap',
                      flexBasis: '100%',
                    }}
                  >
                    {q.comment}
                  </span>
                )}
                <span
                  style={{
                    marginLeft: 'auto',
                    display: 'inline-flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: 2,
                  }}
                >
                  <span style={{ color: 'var(--fg-4)' }}>{formatDateTime(q.createdAt)}</span>
                  <span
                    style={{
                      fontSize: 11,
                      color: expired ? 'var(--status-error-fg)' : 'var(--fg-3)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    {expired && <Icon name="timer-off" size={11} />}
                    {expired
                      ? t('requests:carriers.bid.notActualSince', {
                          date: formatDateTime(validUntil),
                        })
                      : t('requests:carriers.bid.validUntil', {
                          date: formatDateTime(validUntil),
                        })}
                  </span>
                </span>
                {isWinner && <RateHighlight label={t('requests:carriers.winner')} />}
                {isCheapest && !isWinner && (
                  <RateHighlight label={t('requests:carriers.cheapest')} />
                )}
                {canPick && q.scope === 'REQUEST' && (
                  <Button kind="secondary" size="sm" icon="crown" onClick={() => onPickBid(q)}>
                    {t('common:actions.select')}
                  </Button>
                )}
                {canManage && (
                  <>
                    <Button kind="ghost" size="sm" onClick={() => onEditBid(q)}>
                      {t('common:actions.edit')}
                    </Button>
                    <Button kind="ghost" size="sm" onClick={() => onDeleteBid(q)}>
                      {t('common:actions.delete')}
                    </Button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        !isRejected && (
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--fg-3)' }}>
            {t('requests:carriers.noBids')}
          </div>
        )
      )}

      <CarrierInsuranceSection
        requestId={requestId}
        request={request}
        bid={c}
        insuranceRequested={request.insuranceRequested ?? false}
        cargoValue={request.insuranceCargoValue ?? null}
        cargoCurrency={request.insuranceCargoCurrency ?? null}
        canManage={canManage}
      />

      {showCarrierActions && (
        <div
          style={{
            borderTop: '1px solid var(--border-2)',
            paddingTop: 10,
            marginTop: 10,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          {!isRejected && (
            <>
              {/* WHY (#703): the bid form modal can only create primary
                  (`scope = REQUEST`) bids, and primaries are unique
                  per carrier. Once one exists, the "Add bid" path
                  would only trigger a 409 DOMAIN_CONFLICT. Until the
                  override editor lands, hide the affordance for
                  carriers that already have a primary so the UI
                  stays honest. */}
              {!c.bids.some((b) => b.scope === 'REQUEST') && (
                <Button kind="primary" size="sm" icon="plus" onClick={onAddBid}>
                  {t('requests:carriers.addBid')}
                </Button>
              )}
              {canManage && (
                <Button kind="ghost" size="sm" onClick={onReject}>
                  {t('requests:carriers.reject')}
                </Button>
              )}
            </>
          )}
          {((canManage && c.state !== 'pending') || (canRevive && isRejected)) && (
            <Button kind="ghost" size="sm" onClick={onReset}>
              {t('requests:carriers.reset')}
            </Button>
          )}
          {canManage && (
            <Button kind="danger" size="sm" onClick={onDelete}>
              {t('common:actions.delete')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
